import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, X as XIcon } from "lucide-react";
import { API } from "@/config";

/**
 * Sticky promo banner shown at the top of the public site. Loads the currently
 * active promos from /api/promos/active and rotates through them every 6s. If
 * the customer dismisses the banner, we remember that for the session so we
 * don't nag them on every page load.
 */
export const PromoBanner = () => {
  const [promos, setPromos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("promo_banner_dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/promos/active`).then(({ data }) => {
      if (!alive) return;
      setPromos(data.promos || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // Push the site nav (and any other `top-0` fixed layers) down while the
    // banner is visible so it never covers the logo or booking CTA. Cleared
    // on unmount or dismissal.
    const visible = !dismissed && promos.length > 0;
    document.documentElement.style.setProperty("--promo-banner-h", visible ? "36px" : "0px");
    return () => document.documentElement.style.setProperty("--promo-banner-h", "0px");
  }, [dismissed, promos.length]);

  useEffect(() => {
    if (promos.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % promos.length), 6000);
    return () => clearInterval(t);
  }, [promos.length]);

  if (dismissed || promos.length === 0) return null;
  const p = promos[idx % promos.length];

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] text-[#0F1E2C] text-center text-xs sm:text-sm font-medium"
      style={{ background: "linear-gradient(90deg, #F4A93A 0%, #FFD166 50%, #F4A93A 100%)" }}
      data-testid="promo-banner"
    >
      <div className="container flex items-center justify-center gap-2 sm:gap-3 py-2 pr-8 relative">
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
        <Link to="/deals" className="hover:underline truncate" data-testid="promo-banner-link">
          <span className="font-semibold">{p.banner_headline}</span>
          {p.subtitle ? <span className="hidden sm:inline text-[#1a0a03]/80"> — {p.subtitle}</span> : null}
        </Link>
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            try { sessionStorage.setItem("promo_banner_dismissed", "1"); } catch { /* ignore */ }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#1a0a03] hover:text-black"
          aria-label="Dismiss promo banner"
          data-testid="promo-banner-dismiss"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
